import React from 'react';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Button } from 'primereact/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fas } from '@fortawesome/free-solid-svg-icons';

import ThemePicker from './ThemePicker';
import './DashboardHeader.scss';

/**
 * Render a nav/sub-tab icon. Supports PrimeIcons ("pi pi-*") via an <i> and
 * FontAwesome solid icons ("fa-solid fa-*") via <FontAwesomeIcon>.
 */
const TabIcon: React.FC<{ icon: string }> = ({ icon }) => {
  if (icon.includes('fa-')) {
    const name = icon
      .split(/\s+/)
      .map((t) => t.trim())
      .filter((t) => t.startsWith('fa-'))
      .map((t) => t.replace(/^fa-/, ''))
      .filter((t) => !['solid', 'regular', 'brands', 'light'].includes(t))
      .pop();
    const def = name
      ? fas[
          `fa${name
            .split('-')
            .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
            .join('')}` as keyof typeof fas
        ]
      : undefined;
    if (def) return <FontAwesomeIcon icon={def} />;
  }
  return <i className={icon} />;
};

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

export interface SelectOption {
  label: string;
  value: string;
  /** Mark the current/in-progress academic year (shows an "In Progress" tag). */
  inProgress?: boolean;
}

export interface NavTab {
  label: string;
  value: string;
  /** Show an external-link icon after the label (e.g. SLAS, SMC). */
  external?: boolean;
}

export interface SubTab {
  label: string;
  value: string;
  /** Optional count badge, e.g. 22 or 10223. */
  count?: number;
  /** PrimeIcons class shown before the label. */
  icon?: string;
  /** Badge tone. */
  tone?: 'danger' | 'warning' | 'muted';
}

export interface HeaderAction {
  label: string;
  icon?: string;
  /**
   * Visual emphasis:
   *  - 'outlined' → white with border (Open Map)
   *  - 'primary'  → solid indigo (Filter)
   *  - 'solid'    → solid orange (Export PDF)
   *  - 'text'     → borderless
   */
  variant?: 'text' | 'outlined' | 'primary' | 'solid' | 'toggle';
  onClick?: () => void;
  /** Optional count badge shown after the label (e.g. active filter count). */
  badge?: number;
}

export interface HeaderFilter {
  /** Unique key for the filter. */
  key: string;
  /** Uppercase label above the dropdown, e.g. "MANAGEMENT". */
  label: string;
  /** Dropdown options. */
  options: SelectOption[];
  /** Current selected values (multi-select). */
  value: string[];
  /** Placeholder when nothing selected. */
  placeholder?: string;
  /** Change handler — receives the selected values. */
  onChange: (value: string[]) => void;
  /** Render the control frozen (visible but non-interactive) — used on tabs
   * where the filter is shown for context but doesn't apply to the data. */
  disabled?: boolean;
}

export interface DashboardHeaderProps {
  // Top bar
  date?: string;
  years?: SelectOption[];
  year?: string;
  onYearChange?: (value: string) => void;
  /** Lock the Year dropdown (e.g. Review Dashboard / Attendance are pinned
   * to the current academic year only). Shows the single current-year
   * option but prevents picking anything else. */
  yearDisabled?: boolean;
  userName?: string;
  designation?: string;
  /** Click handler for the user/Admin item (e.g. open menu / logout). */
  onUserClick?: () => void;

  // Portal identity
  logoSrc?: string;
  department?: string;
  title?: string;
  subtitle?: string;

  // Primary nav tabs
  navTabs?: NavTab[];
  activeNav?: string;
  onNavChange?: (value: string) => void;

  // Header action buttons (Open Map / Filter / Export PDF)
  actions?: HeaderAction[];

  // Breadcrumb + district selector
  breadcrumb?: string;
  districts?: SelectOption[];
  district?: string | null;
  onDistrictChange?: (value: string) => void;

  /** Lock the District dropdown — district/block users have a fixed district. */
  districtLocked?: boolean;
  /** Lock the Block dropdown — block users have a fixed block. */
  blockLocked?: boolean;
  // Block selector — shown only when a district is selected and blocks exist.
  blocks?: SelectOption[];
  block?: string | null;
  onBlockChange?: (value: string) => void;

  // School selector — shown only when a block is selected and schools exist.
  schools?: SelectOption[];
  school?: string | null;
  /**
   * Display text for the school in the breadcrumb trail (e.g. with the UDISE
   * appended). The Dropdown VALUE still uses `school` (the bare name) so
   * selection/matching is unaffected; only the crumb shows this richer label.
   * Falls back to `school` when omitted.
   */
  schoolCrumb?: string | null;
  onSchoolChange?: (value: string) => void;

  // Filters row (applies to all charts & tables)
  filters?: HeaderFilter[];
  onClearFilters?: () => void;

  // Programme filter (School Programme - displayed separately above other filters on Review Dashboard)
  programmeFilter?: {
    value: string | null;
    options: SelectOption[];
    onChange: (value: string | null) => void;
  };

  // Sub-tabs
  subTabs?: SubTab[];
  activeSubTab?: string;
  onSubTabChange?: (value: string) => void;

  // Source toggle (EMIS / UDISE+)
  sources?: SelectOption[];
  source?: string;
  onSourceChange?: (value: string) => void;

  className?: string;
  cardInPalliPaarvai?: string;
  /** Explicitly disables the School dropdown regardless of
   * `cardInPalliPaarvai` — Palli Paarvai's tabs with their own school-level
   * grid (Schools Not Observed / Schools 3+ Times) DO support picking a
   * school, so the blanket "always disabled while Palli Paarvai is open"
   * rule needs an escape hatch for them. Defaults to the old blanket rule
   * when omitted, for every other caller that doesn't pass this. */
  schoolDisabled?: boolean;
}

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────

/**
 * A reusable, responsive dashboard header: top bar (date, year, user),
 * portal identity row with primary nav tabs and action buttons, a breadcrumb
 * with a district selector, and a sub-tab bar with a source toggle.
 * Every section is optional and driven by props.
 */
const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  date,
  years = [],
  year,
  onYearChange,
  yearDisabled = false,
  userName = 'Admin',
  designation = '',
  onUserClick,
  logoSrc,
  department = 'School Education Department',
  title = 'Government of Tamil Nadu',
  subtitle = 'State Education Portal',
  navTabs = [],
  activeNav,
  onNavChange,
  actions = [],
  breadcrumb = 'Tamil Nadu',
  districts = [],
  district = null,
  onDistrictChange,
  districtLocked = false,
  blocks = [],
  block = null,
  onBlockChange,
  blockLocked = false,
  schools = [],
  school = null,
  schoolCrumb = null,
  onSchoolChange,
  programmeFilter,
  filters = [],
  onClearFilters,
  subTabs = [],
  activeSubTab,
  onSubTabChange,
  sources = [],
  source,
  onSourceChange,
  className = '',
  cardInPalliPaarvai,
  schoolDisabled,
}) => {
  const actionClass = (v?: HeaderAction['variant']) => {
    const base = 'p-button-sm dash-header__action';
    if (v === 'solid') return `${base} dash-header__action--solid`;
    if (v === 'primary') return `${base} dash-header__action--primary`;
    if (v === 'toggle')
      return `${base} p-button-outlined dash-header__action--toggle`;
    if (v === 'text') return `${base} p-button-text`;
    return `${base} p-button-outlined dash-header__action--outlined`;
  };
  return (
    <div className={`dash-header ${className}`.trim()}>
      {/* Sticky group: top bar scrolls off, identity/nav row pins to top. */}
      <div className="dash-header__sticky">
        {/* ── Top bar ── */}
        <div className="dash-header__topbar">
          {date && <span className="dash-header__date">{date}</span>}
          <div className="dash-header__topbar-right">
            {userName && (
              <>
                <span className="dash-header__username" title={userName}>
                  <i className="pi pi-user" />
                  <span className="dash-header__username-text">
                    {userName}
                    {designation && (
                      <span className="dash-header__username-desig">
                        ({designation})
                      </span>
                    )}
                  </span>
                </span>
                <span
                  className="dash-header__topbar-divider"
                  aria-hidden="true"
                />
              </>
            )}
            {years.length > 0 && (
              <span className="dash-header__year">
                <span className="dash-header__year-label">Year</span>
                <Dropdown
                  value={year}
                  options={years}
                  onChange={(e) => onYearChange?.(e.value)}
                  disabled={yearDisabled}
                  className={`dash-header__year-dd${
                    yearDisabled ? ' dash-header__year-dd--locked' : ''
                  }`}
                  panelClassName="dash-header__year-panel"
                />
              </span>
            )}
            <button
              type="button"
              className="dash-header__user dash-header__logout"
              onClick={onUserClick}
              title="Logout"
            >
              <i className="pi pi-sign-out" />
              Logout
            </button>
            <ThemePicker />
          </div>
        </div>

        {/* ── Portal identity + nav + actions ── */}
        <div className="dash-header__main">
          <div className="dash-header__brand">
            {logoSrc ? (
              <img src={logoSrc} alt="" className="dash-header__logo" />
            ) : (
              <span className="dash-header__logo dash-header__logo--placeholder">
                <i className="pi pi-building-columns" />
              </span>
            )}
            <div className="dash-header__brand-text">
              <span className="dash-header__department">{department}</span>
              <span className="dash-header__title">{title}</span>
              <span className="dash-header__subtitle">{subtitle}</span>
            </div>
          </div>

          {navTabs.length > 0 && (
            <nav className="dash-header__nav">
              {navTabs.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  className={`dash-header__nav-tab${
                    t.value === activeNav ? ' is-active' : ''
                  }`}
                  onClick={() => onNavChange?.(t.value)}
                >
                  {t.label}
                  {t.external && (
                    <i className="pi pi-external-link dash-header__nav-ext" />
                  )}
                </button>
              ))}
            </nav>
          )}

          {actions.length > 0 && (
            <div className="dash-header__actions">
              {actions.map((a) => (
                <Button
                  key={a.label}
                  type="button"
                  label={a.label}
                  icon={a.icon}
                  className={actionClass(a.variant)}
                  onClick={a.onClick}
                  badge={a.badge ? String(a.badge) : undefined}
                  badgeClassName="dash-header__action-badge"
                />
              ))}
            </div>
          )}
        </div>
      </div>
      {/* end sticky group */}

      {/* ── Breadcrumb + district ── */}
      {(breadcrumb || districts.length > 0) && (
        <div className="dash-header__crumbbar">
          {breadcrumb && (
            <span className="dash-header__crumb">
              <i className="pi pi-home" />
              {/* Root label kept on one line — without this the flexbox
                  squeeze broke "Tamil Nadu" across two lines on narrow
                  viewports while the school segment hogged the row. */}
              {/* District/block-locked users can never go back to the state
                  (Tamil Nadu) level — render the root muted/non-interactive
                  instead of letting it read as a link with no effect. */}
              <span
                className={`dash-header__crumb-root${
                  districtLocked ? ' is-locked' : ''
                }`}
                aria-disabled={districtLocked || undefined}
              >
                {breadcrumb}
              </span>
              {/* Full drill trail: Tamil Nadu > District > Block > School.
                  Each selected level is appended as a crumb so the current
                  location is shown in full. Long segments (school names)
                  truncate with the full value on hover. */}
              {/* Clickable drill levels: each selected level is a crumb the
                  user can click to jump back to it (district → clears block +
                  school; block → clears school). The last/deepest crumb is the
                  current location, so it isn't clickable. */}
              {(
                [
                  { level: 'district' as const, raw: district, show: district },
                  { level: 'block' as const, raw: block, show: block },
                  {
                    level: 'school' as const,
                    raw: school,
                    show: school ? (schoolCrumb ?? school) : null,
                  },
                ].filter((c) => !!c.show) as {
                  level: 'district' | 'block' | 'school';
                  raw: string | null;
                  show: string;
                }[]
              ).map((c, i, arr) => {
                const isLast = i === arr.length - 1;
                // The district crumb only ever clears BLOCK + SCHOOL (it
                // re-selects the same district), so it's a real escape of the
                // lock ONLY for block-locked users — a plain district-locked
                // user is still allowed to browse other blocks within their
                // own district. The block crumb never escapes either lock (it
                // only clears school), so it's unaffected.
                const escapesLock = c.level === 'district' && blockLocked;
                const clickable = !isLast && !escapesLock;
                const jump = () => {
                  if (!clickable) return;
                  if (c.level === 'district') {
                    // Re-selecting the district clears block + school.
                    onBlockChange?.('');
                    onSchoolChange?.('');
                    onDistrictChange?.(c.raw ?? '');
                  } else if (c.level === 'block') {
                    onSchoolChange?.('');
                    onBlockChange?.(c.raw ?? '');
                  }
                };
                return (
                  <span
                    key={`${c.show}-${i}`}
                    className="dash-header__crumb-trail"
                  >
                    <i
                      className="pi pi-angle-right dash-header__crumb-sep"
                      aria-hidden="true"
                    />
                    <span
                      className={`dash-header__crumb-text${
                        clickable ? ' is-clickable' : ''
                      }`}
                      title={c.show}
                      aria-disabled={!clickable && !isLast ? true : undefined}
                      {...(clickable
                        ? {
                            role: 'button',
                            tabIndex: 0,
                            onClick: jump,
                            onKeyDown: (e: React.KeyboardEvent) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                jump();
                              }
                            },
                          }
                        : {})}
                    >
                      {c.show}
                    </span>
                  </span>
                );
              })}
            </span>
          )}
          {/* Geography dropdowns. Needs a real class: as a bare <div> it had
              no layout, so the three dropdowns and their separators could not
              wrap and overflowed instead. */}
          <div className="dash-header__geo">
            {districts.length > 0 && (
              <Dropdown
                value={district}
                options={districts}
                onChange={(e) => onDistrictChange?.(e.value)}
                placeholder="Select District"
                disabled={districtLocked}
                className={`dash-header__district${districtLocked ? ' dash-header__district--locked' : ''}`}
                panelClassName="dash-header__district-panel"
                filter
                showClear={!districtLocked}
              />
            )}
            {district && blocks.length > 0 && (
              <span className={`dash-header__geo-item ${!block ? 'me-4' : ''}`}>
                <i
                  className="pi pi-angle-right dash-header__crumb-sep dash-header__geo-sep"
                  aria-hidden="true"
                />
                <Dropdown
                  value={block}
                  options={blocks}
                  onChange={(e) => onBlockChange?.(e.value)}
                  placeholder="Select Block"
                  disabled={blockLocked}
                  className={`dash-header__district${blockLocked ? ' dash-header__district--locked' : ''}`}
                  panelClassName="dash-header__district-panel"
                  filter
                  showClear={!blockLocked}
                />
              </span>
            )}
            {district && block && schools.length > 0 && (
              <span className="dash-header__geo-item dash-header__geo-item--school">
                <i
                  className="pi pi-angle-right dash-header__crumb-sep dash-header__geo-sep"
                  aria-hidden="true"
                />
                <Dropdown
                  value={school}
                  options={schools}
                  onChange={(e) => onSchoolChange?.(e.value)}
                  disabled={
                    schoolDisabled ?? cardInPalliPaarvai === 'Palli Paarvai'
                  }
                  placeholder="Select School"
                  className="dash-header__district_school"
                  panelClassName="dash-header__district-panel"
                  filter
                  showClear
                  // No JS truncation here: a fixed character cut-off clipped
                  // names even when the box had room, and didn't adapt when
                  // the box narrowed. The label's CSS ellipsis trims to
                  // whatever actually fits; `title` carries the full name.
                  valueTemplate={(option, props) =>
                    option ? (
                      <span
                        className="dash-header__geo-value"
                        title={option.label ?? option}
                      >
                        {option.label ?? option}
                      </span>
                    ) : (
                      <span>{props.placeholder}</span>
                    )
                  }
                />
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── Filters row (applies to all charts & tables) ── */}
      {(filters.length > 0 || programmeFilter) && (
        <div className="dash-header__filters">
          <div className="dash-header__filters-title">
            <i className="pi pi-sliders-h" />
            Filters — apply to all charts &amp; tables
          </div>

          {/* Programme Filter (School Programme chips) */}
          {/* {programmeFilter && (
            <div className="dash-header__filters-section dash-header__filters-section--programme">
              <label className="dash-header__filters-section-label">
                SCHOOL PROGRAMME
              </label>
              <div className="dash-header__programme-chips">
                {programmeFilter.options.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`dash-header__programme-chip${
                      programmeFilter.value === opt.value ? ' is-active' : ''
                    }`}
                    onClick={() => {
                      programmeFilter.onChange(
                        programmeFilter.value === opt.value ? null : opt.value
                      );
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )} */}

          {/* Management, Category, School Type Filters */}
          {filters.length > 0 && (
            <div className="dash-header__filters-grid">
              {filters.map((f) => (
                <div className="dash-header__filter" key={f.key}>
                  <label className="dash-header__filter-label">{f.label}</label>
                  <MultiSelect
                    value={f.value}
                    options={f.options}
                    onChange={(e) => f.onChange(e.value)}
                    placeholder={f.placeholder}
                    disabled={f.disabled}
                    className="dash-header__filter-dd"
                    panelClassName="dash-header__filter-panel"
                    display="comma"
                    filter
                    maxSelectedLabels={2}
                    selectedItemsLabel="{0} selected"
                  />
                </div>
              ))}
              {/* Clear All appears ONLY when at least one filter has a
                  selection — previously it showed (and looked active) even with
                  nothing selected. */}
              {onClearFilters && filters.some((f) => f.value.length > 0) && (
                <button
                  type="button"
                  className="dash-header__clear-filters"
                  onClick={onClearFilters}
                >
                  <i className="pi pi-refresh" />
                  Clear All
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Sub-tabs + source toggle ── */}
      {(subTabs.length > 0 || sources.length > 0) && (
        <div className="dash-header__subbar">
          <div className="dash-header__subtabs">
            {subTabs.map((t) => (
              <button
                key={t.value}
                type="button"
                className={`dash-header__subtab${
                  t.value === activeSubTab ? ' is-active' : ''
                }`}
                onClick={() => onSubTabChange?.(t.value)}
              >
                {t.icon && <TabIcon icon={t.icon} />}
                {t.label}
                {t.count != null && (
                  <span
                    className={`dash-header__subtab-count dash-header__subtab-count--${
                      t.tone ?? 'muted'
                    }`}
                  >
                    ({t.count.toLocaleString('en-IN')})
                  </span>
                )}
              </button>
            ))}
          </div>

          {sources.length > 0 && (
            <div className="dash-header__source">
              {sources.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  className={`dash-header__source-btn${
                    s.value === source ? ' is-active' : ''
                  }`}
                  onClick={() => onSourceChange?.(s.value)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DashboardHeader;
